import { ProductRenderer } from '../ProductRenderer';
import {
	Box3,
	Box3Helper,
	BufferGeometry,
	Mesh,
	MeshStandardMaterial,
	Object3D,
} from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { CreatorAccessory } from '@/types/creator';
import {
	AccessoryHostingSlot,
	AccessorySlot,
	AxisIndex,
	SerializedAccessory,
	UserDataProp,
} from '../types';
import { UUID } from '@/types/types';
import { RenderUtils } from '../utils/RenderUtils';
import { getAxes } from '@/utils/utilities';
import { CENTER } from 'three-mesh-bvh';

export class Accessory {
	id: UUID;
	collisionLayers: number[];
	collisionBox = new Box3();
	collisionMesh: Mesh | null = null;
	hostingSlots: AccessoryHostingSlot[] = [];
	objectName: string;
	object: Object3D;
	private collidedAccessories = new Set<UUID>();
	private collisionBoxHelper: Box3Helper;
	private _isColliding = false;
	private creatorAccessory: CreatorAccessory;
	private _selectedSlot: AccessorySlot | null = null;
	private materials: MeshStandardMaterial[] = [];

	constructor(
		private renderer: ProductRenderer,
		creatorAccessory: CreatorAccessory,
		object: Object3D
	) {
		this.id = creatorAccessory.refId;

		this.collisionLayers = creatorAccessory.accessory.collisionLayers;

		object.visible = true;
		this.object = object;
		this.objectName = creatorAccessory.accessory.slug;
		this.renderer.product.add(this.object);
		this.object.updateMatrixWorld(true);

		this.object.traverse((child) => {
			if (!RenderUtils.isMesh(child)) return;
			const clone = (child.material as MeshStandardMaterial).clone();
			this.materials.push(clone);
			child.material = clone;
		});

		this.setHostingSlots();

		for (const material of this.materials) {
			material.emissive.set(0xff0000);
			material.emissiveIntensity = 0;
		}

		this.creatorAccessory = creatorAccessory;

		this.object.position.set(...this.creatorAccessory.axesPositions);

		this.updateBox3();

		this.collisionBoxHelper = new Box3Helper(this.collisionBox, 0xff0000);

		if (this.renderer.isDev) this.renderer.product.add(this.collisionBoxHelper);

		if (!!this.collisionLayers.length) this.computeCollisionMesh();
	}

	get isColliding() {
		return this._isColliding;
	}

	set isColliding(value: boolean) {
		this._isColliding = value;

		for (const material of this.materials) {
			material.emissiveIntensity = value ? 1 : 0;
		}
	}

	get selectedSlot() {
		return this._selectedSlot;
	}

	set selectedSlot(slot: AccessorySlot | null) {
		this._selectedSlot = slot;

		if (!slot) return;

		//@ts-ignore
		this.object.position[getAxes()[slot.axis]] = slot.position[slot.axis];

		this.updateBox3();
	}

	private setHostingSlots() {
		this.object.traverse((child) => {
			if (!child.userData[UserDataProp.IS_ACCESSORY_HOST]) return;

			const childAccessoryName =
					child.userData[UserDataProp.CHILD_ACCESSORY_NAME],
				adjustAxis = child.userData[UserDataProp.ADJUST_AXIS];

			if (!childAccessoryName || adjustAxis === undefined) return;

			this.hostingSlots.push({
				childAccessoryName,
				adjustAxis,
				object: child,
			});
		});
	}

	private validateStatus() {
		this.isColliding = !!this.collidedAccessories.size;
	}

	private computeCollisionMesh() {
		const geometries: BufferGeometry[] = [];
		const groupWorldInverse = this.object.matrixWorld.clone().invert();

		this.object.traverse((child) => {
			if (!RenderUtils.isMesh(child)) return;

			const clone = child.geometry.clone();
			clone.applyMatrix4(child.matrixWorld);
			clone.applyMatrix4(groupWorldInverse);

			geometries.push(clone);
		});

		const mergedGeometry = mergeGeometries(geometries);

		mergedGeometry.computeBoundsTree({
			maxLeafSize: 1,
			strategy: CENTER,
		});

		this.collisionMesh = new Mesh(mergedGeometry);
	}

	private updateBox3() {
		this.object.updateMatrixWorld();
		this.collisionBox.setFromObject(this.object, true);
	}

	getCollidedAccessories() {
		return Array.from(this.collidedAccessories);
	}

	addCollidedAccessories(...ids: UUID[]) {
		for (const id of ids) {
			this.collidedAccessories.add(id);
		}

		this.validateStatus();
	}

	removeCollidedAccessories(...ids: UUID[]) {
		for (const id of ids) {
			this.collidedAccessories.delete(id);
		}

		this.validateStatus();
	}

	setAxisPosition(axis: AxisIndex, position: number) {
		const axisName = getAxes()[axis];
		// @ts-ignore
		this.object.position[axisName] = position;

		this.updateBox3();
	}

	load(data: SerializedAccessory) {
		this.id = data.id;
		this.collisionLayers = data.collisionLayers;

		this.collidedAccessories = new Set<UUID>(data.collidedAccessories);
		this.validateStatus();

		if (data.selectedSlotId) {
			const slots = this.creatorAccessory.slotsData.slots.find(
				(slot) => slot.id === data.selectedSlotId
			);
			if (slots) this.selectedSlot = slots;
		}

		this.object.position.fromArray(data.position);
		this.updateBox3();
	}

	export(): SerializedAccessory {
		return {
			id: this.id,
			collisionLayers: this.collisionLayers,
			selectedSlotId: this.selectedSlot?.id || null,
			position: this.object.position.toArray(),
			collidedAccessories: this.getCollidedAccessories(),
		};
	}

	dispose() {
		this.renderer.product.remove(this.object);
		this.collisionBoxHelper.removeFromParent();
		this.collisionBoxHelper.dispose();
		RenderUtils.disposeObject(this.object);
	}
}
